/**
 * The small component kit built to product guide §9, on the prototype's own classes:
 * pop-up (header / body / footer, ~560 px or ~860 px wide, a bottom sheet on a phone), side panel
 * (~480–520 px, full screen on a phone), toast, chips, avatars, filter pills, page head and the
 * loading / empty / error states every list needs.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { HealthBand, Priority, UserRef } from '@amplispot/contracts';
import { ApiFailure } from '../api';
import { LabelInfo } from '../shell/Help';

// ---------------------------------------------------------------- overlays

function useEscape(onClose: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
}

/** Adds the `on` class one frame after mount so the prototype's slide/fade transitions run. */
function useShown() {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(r);
  }, []);
  return shown;
}

export function Modal(props: {
  title: ReactNode;
  sub?: ReactNode;
  avatar?: ReactNode;
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  labelledBy?: string;
  /** Extra class on the body (`.mx-b`), e.g. Meetings' `mv-recording` phone layout. */
  bodyClass?: string;
}) {
  useEscape(props.onClose);
  return createPortal(
    <>
      <div className="scrim on" onClick={props.onClose} />
      <div className={`modal roomy auto on${props.wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={typeof props.title === 'string' ? props.title : undefined}>
        <div className="mx-h">
          {props.avatar}
          <div style={{ minWidth: 0 }}>
            <h2>
              {props.title}
              {typeof props.title === 'string' ? <LabelInfo text={props.title} /> : null}
            </h2>
            {props.sub ? <p>{props.sub}</p> : null}
          </div>
          <button className="mx-x" onClick={props.onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className={`mx-b${props.bodyClass ? ` ${props.bodyClass}` : ''}`}>{props.children}</div>
        {/* null: no footer band (prototype pop-ups whose actions sit in the body, e.g. Import history before a file). */}
        {props.footer === null ? null : <div className="mx-f split">{props.footer}</div>}
      </div>
    </>,
    document.body,
  );
}

export function Drawer(props: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEscape(props.onClose);
  const shown = useShown();
  return createPortal(
    <>
      <div className="scrim on" onClick={props.onClose} />
      <aside className={`drawer${shown ? ' on' : ''}`} aria-label={props.title} role="dialog" aria-modal="true">
        <div className="dh">
          <h2>
            {props.title}
            <LabelInfo text={props.title} />
          </h2>
          <button className="x" onClick={props.onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="db">{props.children}</div>
        {props.footer ? <div className="df">{props.footer}</div> : null}
      </aside>
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------- toast

const ToastCtx = createContext<(m: string) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState('');
  const [on, setOn] = useState(false);
  const t = useRef<number | undefined>(undefined);
  const show = useCallback((m: string) => {
    setMsg(m);
    setOn(true);
    window.clearTimeout(t.current);
    t.current = window.setTimeout(() => setOn(false), 2400);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className={`toast${on ? ' on' : ''}`} role="status" aria-live="polite">
        {msg}
      </div>
    </ToastCtx.Provider>
  );
}

// ---------------------------------------------------------------- people and chips

/** Prototype `av()`: a round initials badge; `size` 26 in lists, 30 by default. */
export function Avatar({ user, size }: { user: Pick<UserRef, 'name' | 'initials'>; size?: number }) {
  const style: CSSProperties | undefined = size ? { width: size, height: size, fontSize: Math.round(size * 0.42) } : undefined;
  return (
    <span className="av" title={user.name} style={style}>
      {user.initials}
    </span>
  );
}

const PRIO: Record<Priority, { n: string; w: number }> = {
  high: { n: 'High', w: 3 },
  med: { n: 'Medium', w: 2 },
  low: { n: 'Low', w: 1 },
};
export const priorityLabel = (p: Priority) => PRIO[p].n;

/** Prototype `prioDot`: a small ▲ after a high-priority client's name (nothing for medium or low). */
export function PrioMark({ p }: { p: Priority }) {
  return p === 'high' ? (
    <span className="pm" title="High priority client">
      ▲
    </span>
  ) : null;
}

/** Prototype `prioChip()`: three signal bars + label, `pc-high|med|low`. */
export function PriorityChip({ p, onClick }: { p: Priority; onClick?: () => void }) {
  const inner = (
    <>
      <span className="pbars">
        {[1, 2, 3].map((i) => (
          <i key={i} className={i <= PRIO[p].w ? 'f' : ''} />
        ))}
      </span>
      {PRIO[p].n}
    </>
  );
  return onClick ? (
    <button
      className={`pc pc-${p} ed`}
      title="Change priority"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {inner}
    </button>
  ) : (
    <span className={`pc pc-${p}`}>{inner}</span>
  );
}

const BAND: Record<HealthBand, { cls: 'hot' | 'warm' | 'cold'; label: string; colour: string }> = {
  healthy: { cls: 'hot', label: 'Healthy', colour: '#16A34A' },
  watch: { cls: 'warm', label: 'Watch', colour: '#D97706' },
  at_risk: { cls: 'cold', label: 'At risk', colour: '#DC2626' },
};
export const bandInfo = (b: HealthBand) => BAND[b];

/** Prototype `hchip()`. With no score yet it says so in words (never colour alone, guide §9). */
export function HealthChip({ band, score }: { band: HealthBand | null; score: number | null }) {
  if (!band || score === null)
    return (
      <span className="chip" title="The health score starts once usage and activity are recorded">
        No score yet
      </span>
    );
  return (
    <span className={`chip ${BAND[band].cls}`}>
      {BAND[band].label} <span className="mono">{score}</span>
    </span>
  );
}

// ---------------------------------------------------------------- filters

export function Pills<T extends string>(props: { options: [T, string, (number | string)?][]; value: T; onChange: (v: T) => void }) {
  return (
    <>
      {props.options.map(([k, label, n]) => (
        <button key={k} className={`fp${props.value === k ? ' on' : ''}`} onClick={() => props.onChange(k)} aria-pressed={props.value === k}>
          {label}
          {n != null ? <span className="n">{n}</span> : null}
        </button>
      ))}
    </>
  );
}

export function Seg<T extends string>(props: { options: [T, string][]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="seg">
      {props.options.map(([k, label]) => (
        <button key={k} className={props.value === k ? 'on' : ''} onClick={() => props.onChange(k)} aria-pressed={props.value === k}>
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- multi-select filter (operator request 18 Oct)

export type FilterOption<T extends string = string> = { value: T; label: string; count?: number | string };

/** What the trigger says: "Client: All", the one name when one is picked, else "Client: 3 selected". */
export function filterSummary(label: string, options: readonly FilterOption[], value: readonly string[], allLabel = 'All'): string {
  if (!value.length) return `${label}: ${allLabel}`;
  if (value.length === 1) return options.find((o) => o.value === value[0])?.label ?? `${label}: 1 selected`;
  return `${label}: ${value.length} selected`;
}

const SEARCH_OVER = 10;
const PHONE = '(max-width: 760px)';

/**
 * A filter that would be a long or growing row of chips (more than 10 options, data-built lists such as clients,
 * people, services or months, or anything that wraps past 2 rows) as one dropdown: a trigger pill, then a popover of
 * checkboxes with a search box over 10 options, Select all / Clear, and the keyboard (arrows move, Space toggles,
 * Esc closes). `value` empty means All. `single` is for a filter the API takes one value for: picking one replaces
 * the choice and closes; `single required` always keeps one picked (no Clear), for a picker of the one record shown.
 * On a phone it opens as a bottom sheet. The screen keeps the value in its URL as before.
 */
export function MultiSelectFilter<T extends string>(props: {
  label: string;
  options: readonly FilterOption<T>[];
  value: readonly T[];
  onChange: (v: T[]) => void;
  single?: boolean;
  required?: boolean;
  /** At most this many picked (an API limit); further options are disabled and the popover says why. */
  max?: number;
  allLabel?: string;
  disabled?: boolean;
}) {
  const { label, options, value, onChange, single, allLabel = 'All' } = props;
  const required = !!(single && props.required);
  const max = single ? undefined : props.max;
  const full = max != null && value.length >= max;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; minWidth: number; maxHeight: number } | null>(null);
  const [phone, setPhone] = useState(false);
  const trig = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const id = useId();
  const searchable = options.length > SEARCH_OVER;
  const needle = q.trim().toLowerCase();
  const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const picked = new Set<string>(value);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setQ('');
    if (refocus) trig.current?.focus();
  }, []);

  const place = useCallback(() => {
    const r = trig.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.max(r.width, 260);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    // Fixed, so it never scrolls into view: fit it to the room below the trigger, or open upwards when a filter low on
    // the page has more room above (Team › Record's Kind under the person table; t-da3a924a).
    const below = window.innerHeight - r.bottom - 14;
    const above = r.top - 14;
    if (below < 240 && above > below) setPos({ bottom: window.innerHeight - r.top + 6, left, minWidth: w, maxHeight: Math.min(420, above) });
    else setPos({ top: r.bottom + 6, left, minWidth: w, maxHeight: Math.min(420, Math.max(below, 160)) });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    setPhone(typeof window.matchMedia === 'function' && window.matchMedia(PHONE).matches);
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pop.current?.contains(t) || trig.current?.contains(t)) return;
      close(false);
    };
    const move = () => place();
    document.addEventListener('mousedown', down);
    window.addEventListener('resize', move);
    window.addEventListener('scroll', move, true);
    (searchable ? search.current : list.current)?.focus();
    return () => {
      document.removeEventListener('mousedown', down);
      window.removeEventListener('resize', move);
      window.removeEventListener('scroll', move, true);
    };
  }, [open, searchable, close, place]);

  useEffect(() => setActive(0), [needle]);

  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  const toggle = (v: T) => {
    if (single) {
      if (!(required && picked.has(v))) onChange(picked.has(v) ? [] : [v]);
      close();
      return;
    }
    if (picked.has(v)) onChange(value.filter((x) => x !== v));
    else if (!full) onChange([...value, v]);
  };
  // With no search, "Select all" is the same as no filter (All), so the URL stays short; with a search it adds the matches.
  const withMatches = Array.from(new Set([...value, ...shown.map((o) => o.value)]));
  const selectAll = () => onChange(needle ? withMatches : []);
  const tooMany = !!needle && max != null && withMatches.length > max;
  const clear = () => onChange([]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(shown.length - 1, a + 1));
      if (document.activeElement === search.current) list.current?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (active === 0 && searchable) search.current?.focus();
      else setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Home' && document.activeElement !== search.current) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && document.activeElement !== search.current) {
      e.preventDefault();
      setActive(shown.length - 1);
    } else if ((e.key === ' ' || e.key === 'Enter') && document.activeElement !== search.current) {
      e.preventDefault();
      const o = shown[active];
      if (o) toggle(o.value);
    } else if (e.key === 'Enter' && document.activeElement === search.current && shown.length) {
      e.preventDefault();
      toggle(shown[active]!.value);
    } else if (e.key === 'Tab' && !phone) {
      close(false);
    }
  };

  const summary = filterSummary(label, options, value, allLabel);
  const panel = open ? (
    <>
      {phone ? <div className="scrim on msf-scrim" onMouseDown={() => close()} /> : null}
      <div
        ref={pop}
        className={`msf-pop${phone ? ' sheet' : ''}`}
        role="dialog"
        aria-label={`Filter by ${label.toLowerCase()}`}
        style={phone || !pos ? undefined : { top: pos.top, bottom: pos.bottom, left: pos.left, minWidth: pos.minWidth, maxHeight: pos.maxHeight }}
        onKeyDown={onKey}
      >
        {phone ? (
          <div className="msf-head">
            <b>{label}</b>
            <button className="btn sm" onClick={() => close()}>
              Done
            </button>
          </div>
        ) : null}
        {searchable ? (
          <input
            ref={search}
            className="in msf-search"
            type="search"
            placeholder={`Search ${label.toLowerCase()}`}
            aria-label={`Search ${label.toLowerCase()}`}
            aria-controls={`${id}-list`}
            aria-activedescendant={shown[active] ? `${id}-o${active}` : undefined}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        ) : null}
        <div
          ref={list}
          id={`${id}-list`}
          className="msf-list"
          role="listbox"
          aria-label={label}
          aria-multiselectable={single ? undefined : true}
          aria-activedescendant={shown[active] ? `${id}-o${active}` : undefined}
          tabIndex={0}
        >
          {shown.length ? (
            shown.map((o, i) => {
              const on = picked.has(o.value);
              const off = full && !on;
              return (
                <div
                  key={o.value}
                  id={`${id}-o${i}`}
                  data-i={i}
                  role="option"
                  aria-selected={on}
                  aria-disabled={off || undefined}
                  className={`msf-opt${i === active ? ' act' : ''}${on ? ' on' : ''}${off ? ' off' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => toggle(o.value)}
                >
                  <span className={`msf-box${single ? ' radio' : ''}`} aria-hidden="true" />
                  <span className="msf-l">{o.label}</span>
                  {o.count != null ? <span className="n">{o.count}</span> : null}
                </div>
              );
            })
          ) : (
            <div className="msf-none">No {label.toLowerCase()} match “{q.trim()}”.</div>
          )}
        </div>
        {full ? <div className="msf-max">Up to {max} at once. Untick one to pick another.</div> : null}
        {required ? null : (
          <div className="msf-foot">
            {single ? null : (
              <button className="btn sm ghost" onClick={selectAll} disabled={(!needle && !value.length) || tooMany} title={tooMany ? `Up to ${max} at once` : undefined}>
                Select all
              </button>
            )}
            <div className="grow" />
            <button className="btn sm ghost" onClick={clear} disabled={!value.length}>
              Clear
            </button>
          </div>
        )}
      </div>
    </>
  ) : null;

  return (
    <>
      <button
        ref={trig}
        type="button"
        className={`fp msf-trig${value.length ? ' on' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${value.length ? summary.replace(`${label}: `, '') : allLabel}`}
        title={value.length > 1 ? options.filter((o) => picked.has(o.value)).map((o) => o.label).join(', ') : summary}
        disabled={props.disabled}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="msf-t">{summary}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" style={{ marginLeft: 6, flex: 'none' }}>
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </>
  );
}

// ---------------------------------------------------------------- states

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty loading" aria-busy="true">
      <span className="spin" aria-hidden="true" />
      {label}
    </div>
  );
}

export function errorText(e: unknown): string {
  if (e instanceof ApiFailure) return e.message;
  return e instanceof Error ? e.message : 'Something went wrong. Try again.';
}

/**
 * Pop-up failures. A 422's field errors go under the inputs the pop-up renders (`shown`, the field paths it has a
 * place for). Anything else, including a field error the pop-up cannot show, is toasted with the server's
 * message, so a refusal is never silent.
 */
export function useFail(shown: readonly string[]) {
  const toast = useToast();
  const [errs, setErrs] = useState<Record<string, string>>({});
  const fail = (e: unknown) => {
    const { fields, say } = splitFailure(e, shown);
    setErrs(fields);
    if (say) toast(say);
  };
  return { errs, setErrs, fail, toast };
}

/** useFail's rule, pure: which field errors go under inputs, and what (if anything) is toasted. */
export function splitFailure(e: unknown, shown: readonly string[]): { fields: Record<string, string>; say: string | null } {
  if (!(e instanceof ApiFailure) || e.status !== 422) return { fields: {}, say: errorText(e) };
  const all = Object.entries(e.fieldErrors);
  const here = all.filter(([k]) => shown.includes(k));
  return { fields: Object.fromEntries(here), say: !all.length || here.length < all.length ? errorText(e) : null };
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="empty">
      <b>This did not load</b>
      <div>{errorText(error)}</div>
      {onRetry ? (
        <button className="btn sm" style={{ marginTop: 12 }} onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

/** A labelled form field (`.fld`) with its error underneath. */
export function Field({ label, error, children, hint }: { label: string; error?: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="fld">
      <span>{label}</span>
      {children}
      {hint && !error ? <small className="fhint">{hint}</small> : null}
      {error ? <small className="ferr">{error}</small> : null}
    </label>
  );
}

/** Prototype `tgl()`: the brand-coloured switch. */
export function Switch({ on, onClick, label, disabled }: { on: boolean; onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{ width: 38, height: 22, borderRadius: 99, background: on ? 'var(--brand)' : '#C9CFDF', position: 'relative', flex: 'none', border: 0, cursor: disabled ? 'default' : 'pointer' }}
    >
      <span style={{ position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: '.15s' }} />
    </button>
  );
}
